import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import DisclaimerConsentModal from "components/SignUp/DisclaimerConsentModal";
import DisclaimerStepOneModal from "components/SignUp/DisclaimerStepOneModal";
import PracticeDetails from "components/SignUp/PracticeDetails";
import PrivacyPolicyModal from "components/SignUp/PrivacyPolicyModal";
import ProgressBar from "components/SignUp/ProgressBar";
import ProviderInformation from "components/SignUp/ProviderInformation";
import SignUpSuccessfully from "components/SignUp/SignUpSuccessfully";
import TermsAndConditionsModal from "components/SignUp/TermsAndConditionsModal";
import { EFirebaseErrorMessage } from "constants/firebaseError";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { Fragment, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { SignUpForm } from "types";
import { signUpFormSchema } from "validations";

const SignUpPage: React.FC = () => {
  const { signUpWithEmail } = useAuth();
  const { showError } = useToast();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [openStepOne, setOpenStepOne] = useState(false);
  const [openConsent, setOpenConsent] = useState(false);
  const [openPrivacy, setOpenPrivacy] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const methods = useForm({
    resolver: yupResolver(signUpFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const handleStepChange = (step: number) => {
    setActiveStep(step);
  };

  const onSubmit = async (data: SignUpForm) => {
    try {
      setIsSubmitting(true);
      delete data.confirmPassword;
      // Ensure npi is always a string (empty string if undefined)
      const signUpData = {
        ...data,
        npi: data.npi ?? "",
      };
      const result = await signUpWithEmail(
        data.email,
        data.password,
        signUpData
      );

      if (result.requiresEmailVerification) {
        setIsSuccess(true);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Sign up failed. Please try again.";
      if (errorMessage === EFirebaseErrorMessage.EMAIL_ALREADY_EXISTS) {
        methods.setError("email", {
          message: "This email has already been used",
        });
      } else if (errorMessage === EFirebaseErrorMessage.INVALID_EMAIL) {
        methods.setError("email", {
          message: "Please enter a valid email address.",
        });
      } else {
        showError("Sign up failed", errorMessage);
      }
    }
  };

  useEffect(() => {
    // Always show step one when entering Sign Up
    setOpenStepOne(true);
    setOpenConsent(false);
    setAgreePrivacy(false);
    setAgreeTerms(false);
  }, []);

  return (
    <Box className="min-h-screen bg-color_F8F8F8 flex flex-col items-center justify-center">
      <Box className="flex flex-col items-center justify-center gap-4">
        {isSuccess ? (
          <SignUpSuccessfully email={methods.getValues("email")} />
        ) : (
          <Fragment>
            <ProgressBar totalSteps={2} activeStep={activeStep} />
            <Box className="p-16 max-w-xl border bg-white rounded-xl space-y-6 border-color_E8E8E9 border-1">
              <FormProvider {...methods}>
                <form
                  onSubmit={methods.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  {activeStep === 1 ? (
                    <ProviderInformation
                      isValid={methods.formState.isValid}
                      isSubmitting={isSubmitting}
                      onPreviousStep={() => handleStepChange(0)}
                    />
                  ) : (
                    <PracticeDetails onNextStep={() => handleStepChange(1)} />
                  )}
                </form>
              </FormProvider>
            </Box>

            {/* Disclaimer Modals */}
            <DisclaimerStepOneModal
              open={openStepOne}
              onContinue={() => {
                setOpenStepOne(false);
                setOpenConsent(true);
              }}
            />
            <DisclaimerConsentModal
              open={openConsent}
              onBack={() => {
                setOpenConsent(false);
                setOpenStepOne(true);
              }}
              onAgree={() => setOpenConsent(false)}
              onOpenPrivacy={() => setOpenPrivacy(true)}
              onOpenTerms={() => setOpenTerms(true)}
              valuePrivacyChecked={agreePrivacy}
              valueTermsChecked={agreeTerms}
              onChangePrivacy={setAgreePrivacy}
              onChangeTerms={setAgreeTerms}
            />
            <PrivacyPolicyModal
              open={openPrivacy}
              onClose={() => setOpenPrivacy(false)}
            />
            <TermsAndConditionsModal
              open={openTerms}
              onClose={() => setOpenTerms(false)}
            />
          </Fragment>
        )}
      </Box>
    </Box>
  );
};

export default SignUpPage;
